


















































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import MonitoringKritisIgdSvc from "@/services/MonitoringKritisIgdService";
import { calculateEwss, getEwssColor } from "./EwssCalculator";
import { appModule } from "@/store/modules/app";
import moment from "moment";
import { maskTekananDarah } from "@/utils/tekananDarah";

function composeTd(sistolik: number | null, diastolik: number | null): string {
  const s = sistolik === null || sistolik === undefined ? "" : String(sistolik);
  const d = diastolik === null || diastolik === undefined ? "" : String(diastolik);
  return !s && !d ? "" : `${s}/${d}`;
}

const VITAL_TIDAK_TERUKUR: Record<string, string[]> = {
  jantung: ["td", "nadi", "respirasi", "spo2"],
  nafas: ["respirasi", "spo2"],
};

@Component({
  inject: { isReadOnly: { default: () => () => false } }
})
export default class MonitoringKritisIgdTtvDialog extends Vue {
  @Prop() value!: boolean;
  @Prop() monitoringId!: number;
  @Prop({ default: null }) editItem!: any;

  isReadOnly!: () => boolean;
  get readOnly(): boolean {
    return this.isReadOnly();
  }

  saving = false;
  hydrating = false;

  form: any = {};

  avpuOptions = [
    { text: "Alert", value: "A" },
    { text: "Voice", value: "V" },
    { text: "Pain", value: "P" },
    { text: "Unresponsive", value: "U" },
  ];

  get show() { return this.value; }
  set show(val: boolean) { this.$emit("input", val); }

  get isEdit() { return !!this.editItem; }

  get isHentiJantung() { return this.form.henti_jantung === "y"; }

  get isHentiNafas() { return this.form.henti_nafas === "y"; }

  get isNafasTidakTerukur() { return this.isHentiJantung || this.isHentiNafas; }

  get kategoriHenti(): string {
    if (this.isHentiJantung) return "jantung";
    if (this.isHentiNafas) return "nafas";
    return "n";
  }

  set kategoriHenti(val: string) {
    this.form.henti_jantung = val === "jantung" ? "y" : "n";
    this.form.henti_nafas = val === "nafas" ? "y" : "n";
  }

  get tdParsed(): { sistolik: number | null; diastolik: number | null } {
    const cocok = /^(\d{1,3})\/(\d{1,3})$/.exec(String(this.form.td || "").trim());
    if (!cocok) return { sistolik: null, diastolik: null };
    return { sistolik: Number(cocok[1]), diastolik: Number(cocok[2]) };
  }

  get tdRules() {
    return [
      (v: string) => {
        if (this.isHentiJantung) return true;
        if (!v) return "Tekanan darah harus diisi";
        const cocok = /^(\d{1,3})\/(\d{1,3})$/.exec(v);
        if (!cocok) return "Format tekanan darah: sistole/diastole, misal 120/80";
        if (Number(cocok[1]) > 350 || Number(cocok[2]) > 350) {
          return "Tekanan darah maksimal 350 mmHg";
        }
        return true;
      }
    ];
  }

  onTdInput(val: string) {
    const bersih = maskTekananDarah(val, String(this.form.td || ""));

    this.form.td = bersih;

    if (bersih !== val) {
      const field = this.$refs.tdRef as { lazyValue?: string };
      if (field) field.lazyValue = bersih;
    }
  }

  @Watch("kategoriHenti")
  onKategoriHentiChange(val: string, lama: string) {
    if (this.hydrating) return;

    const baru = VITAL_TIDAK_TERUKUR[val] || [];

    for (const field of VITAL_TIDAK_TERUKUR[lama] || []) {
      if (baru.includes(field)) continue;
      if (field === "td") {
        if (this.form.td === "0/0") this.form.td = "";
      } else if (this.form[field] === 0) {
        this.form[field] = null;
      }
    }

    for (const field of baru) {
      if (field === "td") {
        this.form.td = "0/0";
      } else {
        this.form[field] = 0;
      }
    }
  }

  get gcsTotal() {
    if (this.form.gcs_e && this.form.gcs_v && this.form.gcs_m) {
      return this.form.gcs_e + this.form.gcs_v + this.form.gcs_m;
    }
    return "-";
  }

  get ewssPreview() {
    const terisi = (v: unknown) => v !== null && v !== undefined && v !== "";
    const sistolik = this.tdParsed.sistolik;
    if (
      !terisi(this.form.respirasi) ||
      !terisi(this.form.spo2) ||
      !terisi(sistolik) ||
      !terisi(this.form.nadi) ||
      !terisi(this.form.suhu)
    ) {
      return null;
    }
    const hasil = calculateEwss({
      respirasi: this.form.respirasi,
      spo2: this.form.spo2,
      hasO2Support: !!this.form.o2_support && this.form.o2_support.toLowerCase() !== "room air",
      tdSistolik: sistolik,
      nadi: this.form.nadi,
      kesadaran: this.form.avpu || "A",
      suhu: this.form.suhu,
    });
    return this.isNafasTidakTerukur ? { ...hasil, kategori: "blue" } : hasil;
  }

  get ewssAlertType() {
    if (!this.ewssPreview) return "info";
    const k = this.ewssPreview.kategori;
    if (k === "tinggi" || k === "blue") return "error";
    if (k === "sedang") return "warning";
    return "success";
  }

  @Watch("value")
  onOpen(val: boolean) {
    if (!val) return;

    this.hydrating = true;
    this.resetForm();
    if (this.editItem) {
      // merge over the reset template so every field (incl. I/O cairan) is bound
      this.form = { ...this.form, ...this.editItem };
      if (this.form.waktu) {
        this.form.waktu = moment(this.form.waktu).format("YYYY-MM-DDTHH:mm");
      }
      this.form.henti_jantung = this.editItem.henti_jantung === "y" ? "y" : "n";
      this.form.henti_nafas = this.form.henti_jantung === "n" && this.editItem.henti_nafas === "y" ? "y" : "n";
      this.form.td = composeTd(
        this.editItem.tekanan_darah_sistolik,
        this.editItem.tekanan_darah_diastolik
      );
    }
    this.$nextTick(() => {
      this.hydrating = false;
    });
  }

  resetForm() {
    this.form = {
      waktu: moment().format("YYYY-MM-DDTHH:mm"),
      henti_jantung: "n",
      henti_nafas: "n",
      td: "",
      nadi: null,
      respirasi: null,
      suhu: null,
      spo2: null,
      o2_support: "",
      gcs_e: null,
      gcs_v: null,
      gcs_m: null,
      avpu: "A",
      pupil_kanan_mm: null,
      pupil_kiri_mm: null,
      reaksi_cahaya_kanan: null,
      reaksi_cahaya_kiri: null,
      skor_nyeri: null,
      gds: null,
      urine_output_ml: null,
      intake_oral_ngt_ml: null,
      intake_parenteral_jenis: "",
      intake_parenteral_ml: null,
      output_muntah_ml: null,
      output_drain_ml: null,
      catatan: "",
    };
  }

  close() {
    this.show = false;
    this.resetForm();
  }

  buildPayload(): Record<string, unknown> {
    const data: Record<string, unknown> = { ...this.form };
    delete data.td;

    if (this.isHentiJantung) {
      data.tekanan_darah_sistolik = 0;
      data.tekanan_darah_diastolik = 0;
      data.nadi = 0;
      data.respirasi = 0;
      data.spo2 = 0;
      return data;
    }

    data.tekanan_darah_sistolik = this.tdParsed.sistolik;
    data.tekanan_darah_diastolik = this.tdParsed.diastolik;

    if (this.isHentiNafas) {
      data.respirasi = 0;
      data.spo2 = 0;
    }

    return data;
  }

  async simpan() {
    if (this.readOnly) return;

    const formRef = this.$refs.form as { validate?: () => boolean };
    if (formRef && formRef.validate && !formRef.validate()) return;

    this.saving = true;
    try {
      const payload = this.buildPayload();
      let resp;
      if (this.isEdit) {
        resp = await MonitoringKritisIgdSvc.updateTtv(this.monitoringId, this.editItem.id, payload);
      } else {
        resp = await MonitoringKritisIgdSvc.addTtv(this.monitoringId, payload);
      }
      if (resp?.data?.status == "1") {
        appModule.NoticeDelay(this.isEdit ? "TTV diperbarui" : "TTV ditambahkan");
        // Show alerts if any
        if (resp.data.alerts && resp.data.alerts.length > 0) {
          setTimeout(() => {
            alert("Alerts:\n" + resp.data.alerts.join("\n"));
          }, 500);
        }
        this.$emit("saved");
        this.close();
      }
    } catch { /* handled */ }
    this.saving = false;
  }
}
